import { useState } from "react"
import { CheckCircle2Icon, CoffeeIcon } from "lucide-react"
import { DocsRouterLink as RouterLink } from "~/docs-data/docs-link"
import { captureEvent } from "~/client/posthog.client"
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog"

/**
 * Coming-soon interest capture for unfinished guides. The coffee button is a
 * playful interest signal — tracked through the same `guide_access_requested`
 * event as the other coming-soon requests, so demand per article stays
 * countable in one place. Optional comment, and an email field for anonymous
 * visitors who want to be notified when the article ships.
 */
export function ComingSoonForm({
  slug,
  userEmail,
  loginHref,
  title = "Coming soon",
  subtitle = "This article isn't quite ready yet.",
  body = "We're working on transitioning this over to Pine59 and making sure it meets the quality bar. Want it sooner? Have a colleague bring us a coffee for motivation — or just press the button below and we'll count it.",
}: {
  slug: string
  userEmail?: string
  loginHref: string
  /** Heading, subtext and body — defaulted to the guide copy; override to
   *  reuse this form for other coming-soon surfaces (e.g. country catalogs). */
  title?: string
  subtitle?: string
  body?: string
}) {
  const [email, setEmail] = useState(userEmail ?? "")
  const [comment, setComment] = useState("")
  const [submitted, setSubmitted] = useState(false)

  const isEmailValid = /\S+@\S+\.\S+/.test(email.trim())
  // Email is optional for the coffee signal itself, but if one is typed it
  // has to be valid.
  const canSubmit = !submitted && (email.trim() === "" || isEmailValid)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    captureEvent("guide_access_requested", {
      slug,
      mode: "preview",
      coffee: true,
      email: email.trim() || undefined,
      comment: comment.trim() || undefined,
      authed: !!userEmail,
    })
    setSubmitted(true)
  }

  if (submitted) {
    return (
      <>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <CheckCircle2Icon className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-base leading-tight">
              Coffee ordered ☕
            </h3>
            <p className="text-xs text-muted-foreground">
              Your interest was logged — it genuinely bumps this article up the
              list.
            </p>
          </div>
        </div>
        {(userEmail || isEmailValid) && (
          <p className="text-sm text-muted-foreground">
            We&apos;ll email{" "}
            <span className="font-medium text-foreground">
              {(userEmail ?? email).trim()}
            </span>{" "}
            when this article is ready.
          </p>
        )}
      </>
    )
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
          <CoffeeIcon className="w-5 h-5 text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-semibold text-base leading-tight">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">{body}</p>

      <form onSubmit={submit} className="space-y-3">
        <textarea
          rows={2}
          placeholder="Anything specific you hope this covers? (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          className="w-full px-3 py-2 border rounded-md bg-background text-sm outline-none focus:border-primary resize-none"
        />
        {userEmail ? (
          <p className="text-xs text-muted-foreground">
            We&apos;ll email you at{" "}
            <span className="font-medium text-foreground">{userEmail}</span>{" "}
            when it&apos;s ready.
          </p>
        ) : (
          <>
            <input
              type="email"
              autoComplete="email"
              placeholder="you@example.com — get notified when it's done (optional)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm outline-none focus:border-primary"
            />
            <p className="text-xs text-muted-foreground">
              Or{" "}
              <RouterLink to={loginHref} className="text-primary hover:underline">
                log in
              </RouterLink>{" "}
              to use your account email.
            </p>
          </>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-4 py-2 text-sm font-medium transition-colors"
        >
          <CoffeeIcon className="w-4 h-4" />
          Send coffee for motivation
        </button>
      </form>
    </>
  )
}

/**
 * In-place dialog wrapper — used from the guides index so clicking a
 * coming-soon card keeps you on the page instead of stranding you on a
 * blurred detail page with no way back.
 */
export function ComingSoonDialog({
  slug,
  userEmail,
  loginHref,
  open,
  onOpenChange,
}: {
  slug: string
  userEmail?: string
  loginHref: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {/* The form renders its own visible heading */}
        <DialogTitle className="sr-only">Coming soon</DialogTitle>
        <ComingSoonForm slug={slug} userEmail={userEmail} loginHref={loginHref} />
      </DialogContent>
    </Dialog>
  )
}
